import * as React from 'react';

import { Checkbox, type CheckboxProps } from '../Checkbox';
import { Input } from '../Input';

export type CheckboxGroupProps = {
  label?: string;
  description?: string;
  errorMessage?: string;
  value?: string[];
  onValueChange?: (value: string[]) => void;
  onChange?: (value: string[]) => void;
  children?: React.ReactNode;
  disabled?: boolean;
  wrapperClassName?: string;
  labelClassName?: string;
  errorClassName?: string;
};

function Group(props: CheckboxGroupProps) {
  const {
    label,
    description,
    errorMessage,
    value,
    onValueChange,
    onChange,
    disabled,
    wrapperClassName,
    labelClassName,
    errorClassName,
    children,
  } = props;
  const [selectedValues, setSelectedValues] = React.useState(value || []);
  const isControlled = value !== undefined;
  const state: CheckboxGroupState = React.useMemo(
    () => ({
      value: isControlled ? value || [] : selectedValues,
      isDisabled: disabled,
      isSelected(value: string) {
        return state.value.includes(value);
      },
      setValue(value: string[]) {
        if (isControlled) {
          onValueChange?.(value);
          onChange?.(value);
        } else {
          setSelectedValues(value);
        }
      },
      addValue(value: string) {
        state.setValue([...state.value, value]);
      },
      removeValue(value: string) {
        state.setValue(state.value.filter(v => v !== value));
      },
      toggleValue(value: string) {
        if (state.isSelected(value)) {
          state.removeValue(value);
        } else {
          state.addValue(value);
        }
      },
    }),
    [disabled, isControlled, onChange, onValueChange, selectedValues, value]
  );
  return (
    <Input.Wrapper className={wrapperClassName} asChild>
      <fieldset disabled={disabled}>
        {label && (
          <Input.Label asChild className={labelClassName}>
            <legend>{label}</legend>
          </Input.Label>
        )}
        {description && <Input.Description>{description}</Input.Description>}
        <CheckboxGroupContext.Provider value={state}>{children}</CheckboxGroupContext.Provider>
        {errorMessage && <Input.ErrorMessage className={errorClassName}>{errorMessage}</Input.ErrorMessage>}
      </fieldset>
    </Input.Wrapper>
  );
}

function useGroupState() {
  const state = React.useContext(CheckboxGroupContext);
  if (state === null) {
    throw 'CheckboxGroup.Item must be children of CheckboxGroup.Group';
  }
  return state;
}

type ItemProps = Omit<CheckboxProps, 'value'> & { value: string };

function Item(props: ItemProps) {
  const { value, children, disabled, ...rest } = props;
  const state = useGroupState();
  return (
    <Checkbox
      checked={state.isSelected(value)}
      onCheckedChange={() => state.toggleValue(value)}
      disabled={state.isDisabled || disabled}
      {...rest}
    >
      {children}
    </Checkbox>
  );
}

interface CheckboxGroupState {
  /** Current selected values. */
  readonly value: readonly string[];
  /** Whether the checkbox group is disabled. */
  readonly isDisabled?: boolean;
  /** Whether the checkbox group is read only. */
  readonly isReadOnly?: boolean;
  /** Returns whether the given value is selected. */
  isSelected(value: string): boolean;
  /** Sets the selected values. */
  setValue(value: string[]): void;
  /** Adds a value to the set of selected values. */
  addValue(value: string): void;
  /** Removes a value from the set of selected values. */
  removeValue(value: string): void;
  /** Toggles a value in the set of selected values. */
  toggleValue(value: string): void;
}

const CheckboxGroupContext = React.createContext<CheckboxGroupState | null>(null);

export const CheckboxGroup = { Group, Item, useGroupState };
